// ═══════════════════════════════════════════════════════════════
// ContactReportsReport.jsx
// Path: src/components/reports/ContactReportsReport.jsx
//
// The ReportsView "Contact Reports" tab — cross-donor rollup of all
// contact reports with two views:
//
//   1. Recent Reports — filtered list with date range, officer,
//      meeting type filters. Click row → opens the donor record.
//
//   2. Compliance Gap — active prospects with no contact report
//      in the last 90 days. The "what's slipping" view that boards
//      and EDs always want.
//
// Phase 5 of CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Notebook,
  AlertTriangle,
  Calendar,
  ArrowRight,
  Filter,
  Users,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { contactReportsService } from "../../services/contactReportsService";
import { MEETING_TYPES } from "../../constants/contactReportSections";
import { Card } from "../ui/Card";
import { FormSelect, FormInput } from "../ui/FormControls";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

// ── Helpers ──────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const meetingTypeLabel = (val) =>
  MEETING_TYPES.find((t) => t.value === val)?.label || val;

const meetingTypeColor = (val) => {
  switch (val) {
    case "discovery":    return "#3b82f6";
    case "cultivation":  return "#10b981";
    case "solicitation": return "#f59e0b";
    case "stewardship":  return "#8b5cf6";
    default:             return "#6b7280";
  }
};

const truncate = (text, max) => {
  if (!text) return "";
  return text.length <= max ? text : text.slice(0, max).trim() + "…";
};

// ═══════════════════════════════════════════════════════════════
// ContactReportsReport
// ═══════════════════════════════════════════════════════════════

export const ContactReportsReport = () => {
  const { palette, members, navigate, setPrintContactReportId } = useApp();

  // ── View state ─────────────────────────────────────────────
  const [view, setView] = useState("recent"); // "recent" | "compliance"

  // ── Data state ─────────────────────────────────────────────
  const [reports, setReports] = useState([]);
  const [complianceGap, setComplianceGap] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Filter state (default: last 90 days) ───────────────────
  const [filterFromDate, setFilterFromDate] = useState(daysAgoStr(90));
  const [filterToDate, setFilterToDate] = useState(todayStr());
  const [filterOfficer, setFilterOfficer] = useState("");
  const [filterMeetingType, setFilterMeetingType] = useState("");

  // ── Load officers (admin/director profiles) for the dropdown ──
  // Best effort — if RLS blocks the query for the current user, the
  // dropdown is just empty and the filter UI gracefully omits it.
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("profiles")
      .select("id, full_name, role")
      .in("role", ["admin", "director"])
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          setOfficers(
            data
              .filter((p) => p.full_name && p.full_name.trim())
              .map((p) => ({ value: p.id, label: p.full_name }))
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Loaders ────────────────────────────────────────────────
  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contactReportsService.getAll({
        sinceDate: filterFromDate || undefined,
        untilDate: filterToDate || undefined,
        createdBy: filterOfficer || undefined,
        meetingType: filterMeetingType || undefined,
      });
      setReports(data);
    } catch (err) {
      setError("Could not load contact reports — " + (err.message || "unknown error"));
    } finally {
      setLoading(false);
    }
  }, [filterFromDate, filterToDate, filterOfficer, filterMeetingType]);

  const loadComplianceGap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contactReportsService.getComplianceGap(90);
      setComplianceGap(data);
    } catch (err) {
      setError("Could not load compliance gap — " + (err.message || "unknown error"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on view switch + filter change
  useEffect(() => {
    if (view === "recent") {
      loadReports();
    } else {
      loadComplianceGap();
    }
  }, [view, loadReports, loadComplianceGap]);

  // ── Stats ──────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (view === "recent") {
      const byType = {};
      reports.forEach((r) => {
        byType[r.meetingType] = (byType[r.meetingType] || 0) + 1;
      });
      return { total: reports.length, byType };
    }
    return { total: complianceGap.length };
  }, [view, reports, complianceGap]);

  // ── Navigation handler ─────────────────────────────────────
  const openDonor = (memberId) => {
    if (!memberId) return;
    const member = members.find((m) => m.id === memberId);
    if (member) navigate("memberDetail", member);
  };

  // ── Officer name lookup for table display ──────────────────
  const officerName = (uuid) => {
    if (!uuid) return "—";
    const found = officers.find((o) => o.value === uuid);
    return found ? found.label : uuid.slice(0, 8) + "…";
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      {/* ── View toggle ─────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          padding: 4,
          background: `${palette.accent1}08`,
          borderRadius: 10,
          width: "fit-content",
        }}
      >
        {[
          { id: "recent", label: "Recent Reports", icon: Notebook },
          { id: "compliance", label: "Compliance Gap (90d)", icon: AlertTriangle },
        ].map((v) => {
          const Icon = v.icon;
          const isActive = view === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: isActive ? palette.accent1 : "transparent",
                color: isActive ? "#fff" : palette.text2,
                fontSize: FONT_SIZES.sm,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <Icon size={ICON_SIZES.sm} />
              {v.label}
            </button>
          );
        })}
      </div>

      {/* ── Stat strip ──────────────────────────────────────── */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        {view === "recent" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Total in range
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.total}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {MEETING_TYPES.map((t) => {
                const count = stats.byType[t.value] || 0;
                if (count === 0) return null;
                const color = meetingTypeColor(t.value);
                return (
                  <div
                    key={t.value}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 8,
                      background: `${color}15`,
                      color,
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 700,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {count} {t.label}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <AlertTriangle size={ICON_SIZES.xl} style={{ color: stats.total > 0 ? "#f59e0b" : "#10b981" }} />
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.total}
              </div>
              <div style={{ fontSize: FONT_SIZES.sm, color: palette.text2, fontFamily: FONT_FAMILY }}>
                {stats.total === 0
                  ? "All active prospects have a contact report in the last 90 days"
                  : `active prospect${stats.total === 1 ? "" : "s"} with no contact report in 90+ days`}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ── Filters (only in Recent view) ──────────────────── */}
      {view === "recent" && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Filter size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: palette.text3,
                fontFamily: FONT_FAMILY,
              }}
            >
              Filters
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: officers.length > 0 ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr",
              gap: 12,
            }}
          >
            <FormInput
              label="From"
              type="date"
              value={filterFromDate}
              onChange={setFilterFromDate}
            />
            <FormInput
              label="To"
              type="date"
              value={filterToDate}
              onChange={setFilterToDate}
            />
            <FormSelect
              label="Meeting Type"
              value={filterMeetingType}
              onChange={setFilterMeetingType}
              options={[{ value: "", label: "All types" }, ...MEETING_TYPES]}
            />
            {officers.length > 0 && (
              <FormSelect
                label="Officer"
                value={filterOfficer}
                onChange={setFilterOfficer}
                options={[{ value: "", label: "All officers" }, ...officers]}
              />
            )}
          </div>
        </Card>
      )}

      {/* ── Loading / error ─────────────────────────────────── */}
      {loading && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <span style={{ color: palette.text3, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY }}>
            Loading…
          </span>
        </Card>
      )}

      {error && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
            }}
          >
            {error}
          </div>
        </Card>
      )}

      {/* ── Recent Reports table ────────────────────────────── */}
      {!loading && !error && view === "recent" && (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {reports.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <Notebook size={ICON_SIZES.xxl} style={{ color: palette.text3, marginBottom: 8 }} />
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  color: palette.text2,
                  fontFamily: FONT_FAMILY,
                }}
              >
                No contact reports match these filters.
              </div>
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: FONT_SIZES.sm,
                fontFamily: FONT_FAMILY,
              }}
            >
              <thead>
                <tr style={{ background: `${palette.accent1}08` }}>
                  {["Visit Date", "Donor", "Meeting Type", "Summary", "Officer", "Next Step", "Due"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: palette.text1,
                        borderBottom: `1px solid ${palette.cardBorder}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((report, i) => {
                  const typeColor = meetingTypeColor(report.meetingType);
                  return (
                    <tr
                      key={report.id}
                      onClick={() => setPrintContactReportId(report.id)}
                      title="Click to view full report"
                      style={{
                        background: i % 2 === 0 ? "transparent" : `${palette.accent1}03`,
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${palette.accent1}10`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = i % 2 === 0 ? "transparent" : `${palette.accent1}03`;
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text1,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDate(report.visitDate)}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text1,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          fontWeight: 600,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span>{report.memberName || "—"}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDonor(report.memberId);
                            }}
                            title="Open donor record"
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: palette.accent1,
                              background: `${palette.accent1}12`,
                              border: "none",
                              borderRadius: 4,
                              padding: "1px 6px",
                              cursor: "pointer",
                              fontFamily: FONT_FAMILY,
                            }}
                          >
                            Open →
                          </button>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: `${typeColor}18`,
                            color: typeColor,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {meetingTypeLabel(report.meetingType)}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          maxWidth: 280,
                        }}
                      >
                        {truncate(report.summary, 100)}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {officerName(report.createdBy)}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          maxWidth: 200,
                        }}
                      >
                        {truncate(report.nextStep, 60)}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text3,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDate(report.nextStepDate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* ── Compliance Gap table ────────────────────────────── */}
      {!loading && !error && view === "compliance" && (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {complianceGap.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <Users size={ICON_SIZES.xxl} style={{ color: "#10b981", marginBottom: 8 }} />
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 600,
                  color: palette.text1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                All caught up.
              </div>
              <div
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  marginTop: 4,
                }}
              >
                Every active prospect has a contact report in the last 90 days.
              </div>
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: FONT_SIZES.sm,
                fontFamily: FONT_FAMILY,
              }}
            >
              <thead>
                <tr style={{ background: `${palette.accent1}08` }}>
                  {["Donor", "Pathway", "Last Report", "Days Since", "Director"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: palette.text1,
                        borderBottom: `1px solid ${palette.cardBorder}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {complianceGap.map((row, i) => {
                  const days = row.daysSinceLastContactReport;
                  const daysLabel = days === null ? "Never" : `${days}d`;
                  const daysColor = days === null ? "#dc2626" : days > 180 ? "#dc2626" : "#f59e0b";
                  return (
                    <tr
                      key={row.memberId}
                      onClick={() => openDonor(row.memberId)}
                      style={{
                        background: i % 2 === 0 ? "transparent" : `${palette.accent1}03`,
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${palette.accent1}10`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = i % 2 === 0 ? "transparent" : `${palette.accent1}03`;
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text1,
                          fontWeight: 600,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                        }}
                      >
                        {row.memberName}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          textTransform: "capitalize",
                        }}
                      >
                        {row.pathway || "—"}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.lastContactReportDate
                          ? formatDate(row.lastContactReportDate)
                          : "Never filed"}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: daysColor,
                          fontWeight: 700,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {daysLabel}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.assignedDirector || (
                          <span style={{ color: "#dc2626", fontStyle: "italic" }}>
                            Unassigned
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
};
